import { createContext, Dispatch, PropsWithChildren, Reducer, useContext, useReducer } from 'react'
import _ from 'lodash'

import { SessionState } from 'common/interfaces'
import { useFlowSlug } from 'utils/flow'
import { useLocalStorage } from 'utils/hook'

type Action =
  | { type: 'popStep' }
  | { type: 'goTo'; step: string }
  | { type: 'continueFlowStep'; step: string; payload: Partial<SessionState> }
  | { type: 'pushStep'; step: string }
  | { type: 'setStart'; step: string }
  | { type: 'submit' }
  | { type: 'setIsSubmitting'; isSubmitting: boolean }
  | { type: 'setIsSubmitDisabled'; isSubmitDisabled: boolean }

const SessionStateContext = createContext<SessionState | undefined>(undefined)
SessionStateContext.displayName = 'SessionState'
const SessionDispatchContext = createContext<[Dispatch<Action>, () => void] | undefined>(undefined)
SessionDispatchContext.displayName = 'SessionDispatch'

const stateReducer = (state: SessionState, action: Action): SessionState => {
  switch (action.type) {
    case 'setStart':
      return {
        currentPage: action.step,
        path: [],
        history: [],
        isSubmitted: false,
        isSubmitting: false,
        isSubmitDisabled: false,
      }
    case 'continueFlowStep':
      return {
        ...state,
        ...action.payload,
      }
    case 'pushStep':
      return {
        ...state,
        currentPage: action.step,
        path: [...state.path, state.currentPage],
        history: [...state.history, state.currentPage],
      }
    case 'popStep':
      return {
        ...state,
        currentPage: _.last(state.path.slice(-1)) ?? '',
        path: _.initial(state.path),
        history: [...state.history, state.currentPage],
      }
    case 'goTo':
      return {
        ...state,
        currentPage: action.step,
        path: state.path.slice(0, state.path.indexOf(action.step)),
        history: [...state.history, state.currentPage],
      }
    case 'submit':
      return {
        ...state,
        isSubmitted: true,
        isSubmitting: false,
      }
    case 'setIsSubmitting':
      return {
        ...state,
        isSubmitting: action.isSubmitting,
      }
    case 'setIsSubmitDisabled':
      return {
        ...state,
        isSubmitDisabled: action.isSubmitDisabled,
      }
    default:
      return state
  }
}

const defaultSession: SessionState = {
  currentPage: '',
  path: [],
  history: [],
  isSubmitted: false,
  isSubmitting: false,
  isSubmitDisabled: false,
}

const searchParams = new URLSearchParams(location.search)
const previewID = searchParams.get('preview_id')

function SessionProvider({ children }: PropsWithChildren): JSX.Element {
  const flowSlug = useFlowSlug()
  const [storedSession, saveSession, clearSession] = useLocalStorage<SessionState>(
    `${flowSlug}/session`,
    defaultSession
  )

  const [state, dispatch] = useReducer<Reducer<SessionState, Action>>(
    (state, action) => {
      const newState = stateReducer(state, action)

      saveSession(newState)

      return newState
    },
    {
      ...storedSession,
      ...(previewID !== null && { currentPage: previewID }),
    }
  )

  return (
    <SessionStateContext.Provider value={state}>
      <SessionDispatchContext.Provider value={[dispatch, clearSession]}>
        {children}
      </SessionDispatchContext.Provider>
    </SessionStateContext.Provider>
  )
}

const useSessionState = (): SessionState => {
  const context = useContext(SessionStateContext)
  if (context === undefined) {
    throw new Error('useSessionState must be used within a SessionProvider')
  }

  return context
}

const useSessionDispatch = (): [Dispatch<Action>, () => void] => {
  const context = useContext(SessionDispatchContext)
  if (context === undefined) {
    throw new Error('useSessionDispatch must be used within a SessionProvider')
  }

  return context
}

export { SessionProvider, useSessionState, useSessionDispatch }
